/**
 * useBoardState Hook
 *
 * Manages board state, initialization, matching, and sweep logic for mfersbeheaded game.
 * Extracted from Board.js as part of refactoring effort.
 */

import { useState, useCallback } from 'react';
import CONSTANTS from '../constants';
import {
  horizontalMatchPositions,
  verticalMatchPositions,
  fillWithNewJewels,
  setDifference,
  applyGravity,
  applyGravityEnhanced,
  applySuperPowerUp,
  damageAdjacentBarrels
} from '../gameLogic';
import { isDeadPiece, isNormalPiece, turnAlivePieceDead } from '../MferHead';
import { GameBoard, StateChange, GravityMovement } from '../../../types/games/mfersbeheaded';
import { Position } from '../../../types/common';

interface BoardConfig {
  BOARD_SIZE: number;
  BARRELS_ENABLED: boolean;
  BARREL_POSITIONS: [number, number][];
  POWER_UPS_ENABLED: boolean;
  BOARD_INITIAL_STATE: number[][] | null;
}

interface ItemToClear {
  x: number;
  y: number;
}

interface SwapResult {
  row: number;
  col: number;
}

interface BoardStateChange {
  t: number;
  changes: StateChange[];
  movements?: GravityMovement[];  // Optional: gravity movements for enhanced recording
}

interface UseBoardStateReturn {
  // State
  itemsToClear: ItemToClear[];

  // Board initialization
  initializeBoard: () => [GameBoard, StateChange[]];
  placeBattlefieldBarrels: (board: GameBoard) => StateChange[];

  // Matching logic
  hasHorizontalMatchAt: (board: GameBoard, row: number, col: number) => boolean;
  hasVerticalMatchAt: (board: GameBoard, row: number, col: number) => boolean;
  findSwapForMatch: (board: GameBoard, selectedJewel?: { rowIndex: number; colIndex: number } | null) => SwapResult | null;

  // Sweep logic
  sweepBoardForMatches: (board: GameBoard, livePlay: boolean) => [number, StateChange[]];
  sweepBoard: (
    board: GameBoard,
    playSound: () => void,
    playAdvancedSoundNTimes: (n: number) => void,
    livePlay: boolean,
    elapsedTimeSince: () => number
  ) => Promise<[number, BoardStateChange[]]>;
  computeNDeadPieces: (board: GameBoard) => number;

  // Power-ups
  applySuperPowerUpWithUpdates: (board: GameBoard, rowIndex: number, colIndex: number) => Promise<[number, StateChange[]]>;

  // Items to clear
  addItemToClear: (x: number, y: number) => void;
  clearItems: () => void;
  isJewelToBeCleared: (x: number, y: number) => boolean;
}

export const useBoardState = (
  config: BoardConfig,
  forceUpdate: () => void
): UseBoardStateReturn => {
  const {
    BOARD_SIZE,
    BARRELS_ENABLED,
    BARREL_POSITIONS,
    POWER_UPS_ENABLED,
    BOARD_INITIAL_STATE
  } = config;

  // Items to clear state (for visual effects)
  const [itemsToClear, setItemsToClear] = useState<ItemToClear[]>([]);

  const addItemToClear = useCallback((x: number, y: number): void => {
    setItemsToClear(prevItems => [...prevItems, { x, y }]);
  }, []);

  const clearItems = useCallback((): void => {
    setItemsToClear([]);
  }, []);

  const isJewelToBeCleared = useCallback((x: number, y: number): boolean => {
    return itemsToClear.some(item => item.x === x && item.y === y);
  }, [itemsToClear]);

  /**
   * Initialize the board with random or preset values
   * @returns {Array} [board, stateChanges]
   */
  const initializeBoard = useCallback((): [GameBoard, StateChange[]] => {
    const board: GameBoard = [];
    const stateChanges: StateChange[] = [];
    let queue: number[] = [];

    if (BOARD_INITIAL_STATE !== null) {
      queue = [...BOARD_INITIAL_STATE.flat()];
    } else {
      for (let i = 0; i < BOARD_SIZE * BOARD_SIZE; i++) {
        queue.push(Math.floor(Math.random() * (CONSTANTS.N_MFERHEADS - 1)) + 1);
      }
    }

    for (let i = 0; i < BOARD_SIZE; i++) {
      const row: number[] = [];
      for (let j = 0; j < BOARD_SIZE; j++) {
        const val = queue.shift()!;
        row.push(val);
        stateChanges.push([i, j, val]);
      }
      board.push(row);
    }

    console.log('=== initializeBoard DEBUG ===');
    console.log('Board created:');
    board.forEach((row, i) => {
      console.log(`Row ${i}:`, row.join(', '));
    });
    console.log('State changes created:', stateChanges.length);
    console.log('First 10 state changes:', stateChanges.slice(0, 10));
    console.log('============================');

    return [board, stateChanges];
  }, [BOARD_SIZE, BOARD_INITIAL_STATE]);

  /**
   * Place barrels at specified positions
   * @param {Array} board - The game board
   * @returns {Array} State changes from barrel placement
   */
  const placeBattlefieldBarrels = useCallback((board: GameBoard): StateChange[] => {
    const stateChanges: StateChange[] = [];
    if (BARRELS_ENABLED && BARREL_POSITIONS.length > 0) {
      BARREL_POSITIONS.forEach(([row, col]) => {
        if (row >= 0 && row < BOARD_SIZE && col >= 0 && col < BOARD_SIZE) {
          board[row][col] = 11; // Barrel Full
          stateChanges.push([row, col, 11]);
        }
      });
    }
    return stateChanges;
  }, [BARRELS_ENABLED, BARREL_POSITIONS, BOARD_SIZE]);

  /**
   * Check if there's a horizontal match at a position
   * @param {Array} board - The game board
   * @param {number} row - Row index
   * @param {number} col - Column index
   * @returns {boolean} True if horizontal match exists
   */
  const hasHorizontalMatchAt = useCallback((board: GameBoard, row: number, col: number): boolean => {
    const jewelType = board[row][col];
    let matchCount = 0;

    if (!isNormalPiece(jewelType)) {
      return false;
    }

    for (let i = col; i < BOARD_SIZE; i++) {
      if (board[row][i] === jewelType && isNormalPiece(board[row][i])) matchCount++;
      else break;
    }

    for (let i = col - 1; i >= 0; i--) {
      if (board[row][i] === jewelType && isNormalPiece(board[row][i])) matchCount++;
      else break;
    }

    return matchCount >= 3;
  }, [BOARD_SIZE]);

  /**
   * Check if there's a vertical match at a position
   * @param {Array} board - The game board
   * @param {number} row - Row index
   * @param {number} col - Column index
   * @returns {boolean} True if vertical match exists
   */
  const hasVerticalMatchAt = useCallback((board: GameBoard, row: number, col: number): boolean => {
    const jewelType = board[row][col];
    let matchCount = 0;

    if (!isNormalPiece(jewelType)) {
      return false;
    }

    for (let i = row; i < BOARD_SIZE; i++) {
      if (board[i][col] === jewelType && isNormalPiece(board[i][col])) matchCount++;
      else break;
    }

    for (let i = row - 1; i >= 0; i--) {
      if (board[i][col] === jewelType && isNormalPiece(board[i][col])) matchCount++;
      else break;
    }

    return matchCount >= 3;
  }, [BOARD_SIZE]);

  /**
   * Find a swap that would create a match
   * @param {Array} board - The game board
   * @param {Object|null} selectedJewel - Currently selected jewel {rowIndex, colIndex} or null
   * @returns {Object|null} {row, col} of jewel to swap, or null if no match found
   */
  const findSwapForMatch = useCallback((
    board: GameBoard,
    selectedJewel: { rowIndex: number; colIndex: number } | null = null
  ): SwapResult | null => {
    const copiedBoard = board.map(row => [...row]);

    const startRow = selectedJewel ? selectedJewel.rowIndex : 0;
    const endRow = selectedJewel ? selectedJewel.rowIndex + 1 : BOARD_SIZE;

    let bestSwap: SwapResult | null = null;
    let highestPriority = 0;

    for (let row = endRow - 1; row >= startRow; row--) {
      const startCol = selectedJewel ? selectedJewel.colIndex : 0;
      const endCol = selectedJewel ? selectedJewel.colIndex + 1 : BOARD_SIZE;

      for (let col = startCol; col < endCol; col++) {
        if (board[row][col] === 7) {
          return { row, col };
        }

        const neighbors = [
          { row: row + 1, col: col },
          { row: row - 1, col: col },
          { row: row, col: col - 1 },
          { row: row, col: col + 1 }
        ];

        for (const neighbor of neighbors) {
          if (neighbor.row >= 0 && neighbor.row < BOARD_SIZE && neighbor.col >= 0 && neighbor.col < BOARD_SIZE) {
            const temp = copiedBoard[row][col];
            copiedBoard[row][col] = copiedBoard[neighbor.row][neighbor.col];
            copiedBoard[neighbor.row][neighbor.col] = temp;

            const [horizontalMatches] = horizontalMatchPositions(copiedBoard, row, col, BOARD_SIZE);
            const [verticalMatches] = verticalMatchPositions(copiedBoard, row, col, BOARD_SIZE);

            copiedBoard[neighbor.row][neighbor.col] = copiedBoard[row][col];
            copiedBoard[row][col] = temp;

            const maxMatch = Math.max(horizontalMatches.length, verticalMatches.length);
            if (maxMatch >= 3 && maxMatch > highestPriority) {
              bestSwap = selectedJewel ? { row: neighbor.row, col: neighbor.col } : { row, col };
              highestPriority = maxMatch;
              if (highestPriority === 5) {
                return bestSwap;
              }
            }
          }
        }
      }
    }

    return bestSwap;
  }, [BOARD_SIZE]);

  /**
   * Sweep the board for matches and clear them
   * @param {Array} board - The game board
   * @param {boolean} livePlay - Whether this is live play (affects power-up creation)
   * @returns {Array} [matchCount, stateChanges]
   */
  const sweepBoardForMatches = useCallback((board: GameBoard, livePlay: boolean): [number, StateChange[]] => {
    const matchedPositions = new Set<string>();
    const powerUpFiveRunPosition = new Set<string>();
    const stateChanges: StateChange[] = [];

    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        const [horizontalMatches, isCenterHorizontal] = horizontalMatchPositions(board, row, col, BOARD_SIZE);
        const [verticalMatches, isCenterVertical] = verticalMatchPositions(board, row, col, BOARD_SIZE);

        if (POWER_UPS_ENABLED && livePlay && ((horizontalMatches.length >= 5 && isCenterHorizontal) || (isCenterVertical && verticalMatches.length >= 5))) {
          powerUpFiveRunPosition.add(`${row}-${col}`);
          continue;
        }

        if (POWER_UPS_ENABLED && livePlay && ((horizontalMatches.length >= 3) && (verticalMatches.length >= 3))) {
          powerUpFiveRunPosition.add(`${row}-${col}`);
          continue;
        }

        horizontalMatches.forEach(match => matchedPositions.add(`${match.row}-${match.col}`));
        verticalMatches.forEach(match => matchedPositions.add(`${match.row}-${match.col}`));

        if (horizontalMatches.length !== 0 || verticalMatches.length !== 0) {
          addItemToClear(col, row);
        }
      }
    }

    // Set the focus position for power-ups
    powerUpFiveRunPosition.forEach(pos => {
      const [row, col] = pos.split('-').map(Number);
      board[row][col] = 7;
      stateChanges.push([row, col, board[row][col]]);
    });

    // Clear the matched jewels
    const positionToDelete = setDifference(matchedPositions, powerUpFiveRunPosition);
    const explodedPositions: Position[] = [];

    positionToDelete.forEach(pos => {
      const [row, col] = pos.split('-').map(Number);
      board[row][col] = turnAlivePieceDead(board[row][col]);
      stateChanges.push([row, col, board[row][col]]);
      explodedPositions.push({ row, col });
    });

    // Damage adjacent barrel tiles
    if (BARRELS_ENABLED && explodedPositions.length > 0) {
      const barrelDamageChanges = damageAdjacentBarrels(board, explodedPositions, BOARD_SIZE);
      stateChanges.push(...barrelDamageChanges);
    }

    clearItems();

    return [matchedPositions.size, stateChanges];
  }, [BOARD_SIZE, POWER_UPS_ENABLED, BARRELS_ENABLED, addItemToClear, clearItems]);

  /**
   * Compute number of dead pieces on the board
   * @param {Array} board - The game board
   * @returns {number} Number of dead pieces
   */
  const computeNDeadPieces = useCallback((board: GameBoard): number => {
    let nDeadPieces = 0;
    for (let row = 0; row < board.length; row++) {
      for (let col = 0; col < board[row].length; col++) {
        if (isDeadPiece(board[row][col])) {
          nDeadPieces += 1;
        }
      }
    }
    return nDeadPieces;
  }, []);

  /**
   * Sweep the board repeatedly until no more matches
   * @param {Array} board - The game board
   * @param {Function} playSound - Function to play sound effects
   * @param {Function} playAdvancedSoundNTimes - Function to play advanced sound
   * @param {boolean} livePlay - Whether this is live play
   * @param {Function} elapsedTimeSince - Function to get elapsed time
   * @returns {Array} [totalCleared, stateChanges]
   */
  const sweepBoard = useCallback(async (
    board: GameBoard,
    playSound: () => void,
    playAdvancedSoundNTimes: (n: number) => void,
    livePlay: boolean,
    elapsedTimeSince: () => number
  ): Promise<[number, BoardStateChange[]]> => {
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    const stateChanges: BoardStateChange[] = [];
    let [nClearedThisRound, stateChangesFromSweeps] = sweepBoardForMatches(board, livePlay);

    if (livePlay) {
      stateChanges.push({ t: elapsedTimeSince(), changes: stateChangesFromSweeps });
    }

    let nCleared = nClearedThisRound;
    let shouldContinue = (nCleared > 0 || computeNDeadPieces(board) > 0);

    while (shouldContinue) {
      if (livePlay) {
        playSound();
        await delay(CONSTANTS.TIMING.SOUND_DELAY);

        if (nClearedThisRound > 3) {
          playAdvancedSoundNTimes(1);
          await delay(CONSTANTS.TIMING.SOUND_DELAY);
        }
      }

      const gravityResult = applyGravityEnhanced(board, BOARD_SIZE);
      if (livePlay) {
        stateChanges.push({
          t: elapsedTimeSince(),
          changes: gravityResult.stateChanges,
          movements: gravityResult.movements  // Add movements for enhanced recording
        });
      }

      const [, stateChangesFromJewels] = fillWithNewJewels(board, BOARD_SIZE, CONSTANTS.N_MFERHEADS);
      if (livePlay) {
        stateChanges.push({ t: elapsedTimeSince(), changes: stateChangesFromJewels });
      }

      await delay(CONSTANTS.TIMING.ANIMATION_DELAY);

      [nClearedThisRound, stateChangesFromSweeps] = sweepBoardForMatches(board, livePlay);
      if (livePlay) {
        stateChanges.push({ t: elapsedTimeSince(), changes: stateChangesFromSweeps });
      }

      nCleared += nClearedThisRound;
      shouldContinue = nClearedThisRound > 0 || computeNDeadPieces(board) > 0;
    }

    return [nCleared, stateChanges];
  }, [BOARD_SIZE, sweepBoardForMatches, computeNDeadPieces]);

  /**
   * Apply super power-up and update UI
   * @param {Array} board - The game board
   * @param {number} rowIndex - Row of power-up
   * @param {number} colIndex - Column of power-up
   * @returns {Array} [deadCount, stateChanges]
   */
  const applySuperPowerUpWithUpdates = useCallback(async (
    board: GameBoard,
    rowIndex: number,
    colIndex: number
  ): Promise<[number, StateChange[]]> => {
    const [deadCount, stateChanges] = applySuperPowerUp(board, rowIndex, colIndex, BOARD_SIZE);
    forceUpdate(); // Update the UI
    return [deadCount, stateChanges];
  }, [BOARD_SIZE, forceUpdate]);

  return {
    // State
    itemsToClear,

    // Board initialization
    initializeBoard,
    placeBattlefieldBarrels,

    // Matching logic
    hasHorizontalMatchAt,
    hasVerticalMatchAt,
    findSwapForMatch,

    // Sweep logic
    sweepBoardForMatches,
    sweepBoard,
    computeNDeadPieces,

    // Power-ups
    applySuperPowerUpWithUpdates,

    // Items to clear
    addItemToClear,
    clearItems,
    isJewelToBeCleared,
  };
};

export default useBoardState;
